import { Component, Ref } from 'vue-property-decorator'
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader.vue'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import { GetContextMenuItemsParams, MenuItemDef, RowClickedEvent } from '@ag-grid-enterprise/all-modules'
import CustomerGroupsModal from '@/modules/configurationModule/detail/accounting/billable/customer-groups/components/customer-groups-modal/customer-groups-modal.vue'
import BaseCrudServerSideComponent from '@/shared/resources/base-crud-server-side.component'
import { BaseCrud } from '@/shared/models/cruds.models'
import {
    CustomerGroupCreateRequest, CustomerGroupPatchInBatchRequest,
    CustomerGroupResponse
} from '@/modules/configurationModule/detail/accounting/billable/customer-groups/services/customer-groups.dto'
import {
    CustomerGroupsGridSidepanelComboElements,
    CustomerGroupsGridState, initialCustomerGroupsGridState
} from '@/modules/configurationModule/detail/accounting/billable/customer-groups/components/customer-groups-grid/customer-groups.model'
import { StrictNullable } from '@/shared/types/app.types'
import { EntityIdentifiersRequest, EntityIdentifierWithErrorResponse } from '@/shared/models/http/http.model'
import {
    ConfigurationCustomerResponse
} from '@/modules/configurationModule/detail/local-accounting/billable/clients/services/customer-services/configuration-local-accounting-billable-clients-customer-services.dto'
import {
    ConfigurationListSidePanelEntity
} from '@/modules/configurationModule/shared/components/configuration-list-sidepanel/configuration-list-sidepanel.model'
import ConfigurationListSidepanelComp
    from '@/modules/configurationModule/shared/components/configuration-list-sidepanel/configuration-list-sidepanel'
import ConfigurationListSidepanel
    from '@/modules/configurationModule/shared/components/configuration-list-sidepanel/configuration-list-sidepanel.vue'
import {
    configurationLocalAccountingBillableCustomerService
} from '@/modules/configurationModule/detail/local-accounting/billable/clients/services/customer-services/configuration-local-accounting-billable-clients-customer-services.service'
import { GridUtils } from '@/shared/util/grid-utils'
import { CommonParams } from '@/api/resources/common/shared/models/common.models'
import { PAGINATOR_BIG_PAGE_SIZE } from '@/shared/services/services-constants'

@Component({
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        CustomerGroupsModal,
        ConfigurationListSidepanel
    }
})
export default class CustomerGroupsGrid extends BaseCrudServerSideComponent<CustomerGroupResponse> implements BaseCrud {
    @Ref() readonly customerGroupsModalWrapper!: any
    @Ref() readonly configurationListSidepanel!: ConfigurationListSidepanelComp

    state: CustomerGroupsGridState
    constructor() {
        super()
        this.state = initialCustomerGroupsGridState()
    }

    get canUpdate(): boolean {
        return this.state.selectedRows.length === 1
    }

    get canDelete(): boolean {
        return !!this.state.selectedRows.length
    }

    /// GRID EVENT HANDLERS ///

    onUpdate(customerGroup: CustomerGroupResponse = this.state.gridApi?.getSelectedRows()[0]): void {
        if (customerGroup) {
            this.showModal(this.$tc('configuration.actions.update'), customerGroup, this.customerGroupsModalWrapper)
        }
    }

    async onDelete(): Promise<void> {
        if (this.state.selectedRows.length) {
            const requestDelete: EntityIdentifiersRequest = {
                entityIdentifiers: this.state.selectedRows.map(assay => assay.id)
            }
            await this.deleteOperation(requestDelete)
        }
    }

    onAdd(): void {
        this.showModal(this.$tc('configuration.actions.add'), null, this.customerGroupsModalWrapper)
    }

    async onActivate(): Promise<void> {
        await this.activateDeactivateSelection(true, this.state.selectedRows)
    }

    async onDeactivate(): Promise<void> {
        await this.activateDeactivateSelection(false, this.state.selectedRows)
    }

    getContextMenuItems(params: GetContextMenuItemsParams): MenuItemDef[] {
        return [
            {
                name: this.$tc('configuration.actions.update'),
                cssClasses: this.state.selectedRows.length > 1 ? ['not-show'] : [''],
                action: () => this.onUpdate(params?.node?.data)
            },
            {
                name: this.$tc('configuration.actions.delete'),
                cssClasses: this.state.selectedRows.length < 1 ? ['not-show'] : [''],
                action: this.onDelete
            },
            {
                name: params?.node?.data?.active ? this.$tc('configuration.actions.deactivate') : this.$tc('configuration.actions.activate'),
                cssClasses: this.state.selectedRows.length ? ['not-show'] : [''],
                action: () => this.activateDeactivateSelection(!params?.node?.data?.active, [params?.node?.data])
            },
            {
                name: this.$tc('configuration.actions.activate'),
                cssClasses: this.canActivateBatch ? [''] : ['not-show'],
                action: this.onActivate
            },
            {
                name: this.$tc('configuration.actions.deactivate'),
                cssClasses: this.canDeactivateBatch ? [''] : ['not-show'],
                action: this.onDeactivate
            }
        ]
    }

    onRowClicked(event: RowClickedEvent): void {
        const customerGroupSelected: ConfigurationCustomerResponse = event.data
        this.configurationListSidepanel.show({
            title: customerGroupSelected.name,
            entities: [
                {
                    id: CustomerGroupsGridSidepanelComboElements.CLIENTS,
                    entityKey: 'configuration.sections.client'
                }
            ],
            obtainEntityList: async (entity: ConfigurationListSidePanelEntity): Promise<string[]> => {
                if (entity.id as CustomerGroupsGridSidepanelComboElements === CustomerGroupsGridSidepanelComboElements.CLIENTS) {
                    return this._getCustomerGroupCustomers(customerGroupSelected)
                }
                return []
            }
        })
    }

    /// GRID OPERATIONS ///

    async activateDeactivateSelection(active: boolean, customerGroup: CustomerGroupResponse[]): Promise<void> {
        if (customerGroup?.length) {
            const batchRequests = customerGroup.filter(customerGroup => customerGroup.active !== active)
                .map(customerGroup => ({
                    id: customerGroup.id,
                    active
                }))
            if (await this._patchCustomerGroups(batchRequests)) {
                this.reloadData()
            }
        }
    }

    async createOperation(createRequest: CustomerGroupCreateRequest): Promise<void> {
        if (createRequest) {
            if (await this._createCustomerGroup(createRequest)) {
                this.customerGroupsModalWrapper.hide()
                this.reloadData()
            }
        }
    }

    async patchOperation(batchRequest: CustomerGroupPatchInBatchRequest): Promise<void> {
        if (batchRequest) {
            if (await this._patchCustomerGroups([batchRequest])) {
                this.customerGroupsModalWrapper.hide()
                this.reloadData()
            }
        }
    }

    private async _showModalAdviceWhenDelete(): Promise<boolean> {
        const labelConfirmation: string = this.$tc('configuration.grid.deleteConfirmation')
        const selectedRowsSize: number = this.state?.selectedRows?.length
        const labelGrid: string = this.$tc('configuration.grid.element', selectedRowsSize)
        const labelQuestion: string = this.$tc('configuration.grid.deleteQuestion')
        const labelAcceptAction: string = this.$tc('configuration.actions.delete')
        return this.$_showConfirmationModal(
            `${labelConfirmation} ${selectedRowsSize} ${labelGrid}, <br/> ${labelQuestion}`,
            labelAcceptAction
        )
    }

    async deleteOperation(requestDelete: EntityIdentifiersRequest): Promise<void> {
        if (requestDelete) {
            if (await this._showModalAdviceWhenDelete()) {
                if (await this._deleteCustomerGroups(requestDelete)) {
                    this.reloadData()
                }
            }
        }
    }

    // SERVICE CALLS ///

    private async _getCustomerGroupCustomers(customerGroupSelected: CustomerGroupResponse): Promise<string[]> {
        const params: CommonParams = {
            size: PAGINATOR_BIG_PAGE_SIZE
        }
        const filterModel = {
            'group.id': GridUtils.getNumberFilterModel('equals', customerGroupSelected.id)
        }
        const laboratoryId = this.$store.getters.labId
        const response = await this.$_toCallUnique(configurationLocalAccountingBillableCustomerService.paginator(filterModel, params, laboratoryId))
        return response?.content.map((item: ConfigurationCustomerResponse) => item.name) ?? []
    }

    private async _createCustomerGroup(createRequest: CustomerGroupCreateRequest): Promise<StrictNullable<CustomerGroupResponse>> {
        const response = await this.$_toCallUnique(this.state.service.create(createRequest))
        if (response) {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.created'))
            return response
        }
        return null
    }

    private async _patchCustomerGroups(batchRequests: CustomerGroupPatchInBatchRequest[]): Promise<EntityIdentifierWithErrorResponse[] | null> {
        const response = await this.$_toCallUnique(this.state.service.patch(batchRequests))
        if (response && response.length !== batchRequests.length) {
            const elements = batchRequests.length - response.length
            if (elements > 0) {
                const choice = elements > 1 ? 2 : 1
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', choice, { elements }))
            }
            return response
        }
        return null
    }

    private async _deleteCustomerGroups(requestDelete: EntityIdentifiersRequest): Promise<EntityIdentifierWithErrorResponse[] | null> {
        const ids = requestDelete?.entityIdentifiers
        if (ids?.length) {
            const response = await this.$_toCallUnique(this.state.service.delete(requestDelete))
            if (response) {
                const elements = ids.length - response.length
                if (elements > 0) {
                    const choice = elements > 1 ? 2 : 1
                    this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted', choice, { elements }))
                }
                return response
            }
        }
        return null
    }
}
